import React, { memo, CSSProperties, useCallback } from 'react'
import { styled, keyframes, alpha } from '@mui/material'
import clsx from 'clsx'
import { NoneToVoidFunction } from '$hooks/types'

interface OwnProps {
  isActive: boolean
  isViewed: boolean
  isVideo?: boolean
  duration?: number
  isPaused?: boolean
  onImageComplete?: NoneToVoidFunction
  onClick?: () => unknown
}

const DEFAULT_STORY_DURATION_S = 10

const progress = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(0);
  }
`

function StoryProgress({
  isActive,
  isViewed,
  isVideo,
  duration = DEFAULT_STORY_DURATION_S,
  isPaused,
  onImageComplete,
  onClick,
}: OwnProps) {
  const handleAnimationEnd = useCallback(
    (event: React.AnimationEvent<HTMLElement>) => {
      if (!isVideo && event.animationName === progress.name) {
        onImageComplete?.()
      }
    },
    [onImageComplete, isVideo],
  )

  return (
    <Root
      onClick={onClick}
      aria-hidden
      className={clsx({ paused: isPaused, viewed: isViewed, active: isActive })}
      sx={{
        cursor: onClick ? 'pointer' : undefined,
      }}
    >
      {isActive && (
        <Inner
          style={{ '--progress-duration': `${duration}s` } as CSSProperties}
          className="story-progress__inner"
          onAnimationEnd={handleAnimationEnd}
        />
      )}
    </Root>
  )
}

const Root = styled('span')`
  --progress-duration: ${DEFAULT_STORY_DURATION_S}s;
  flex: 1 1 auto;
  background-color: ${({ theme }) => alpha(theme.palette.neutral000, 0.25)};
  border-radius: 1px;
  margin: 0 2px;
  position: relative;
  overflow: hidden;
  &.viewed {
    background-color: ${({ theme }) => theme.palette.neutral000};
  }
  &.active {
    background-color: ${({ theme }) => alpha(theme.palette.neutral000, 0.25)};
  }
  &.paused {
    animation-play-state: paused;
    .story-progress__inner {
      animation-play-state: paused;
    }
  }
`

const Inner = styled('i')`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.palette.neutral000};
  border-radius: 1px;
  transform-origin: 0 50%;
  transform: translateX(-100%);
  animation: ${progress} var(--progress-duration) linear forwards;
`

export default memo(StoryProgress)
